import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import volunteerService from '../services/volunteerService';
import './VolunteerRegistration.css';

const SEVA_OPTIONS = [
  'Youth Camps', 'Music Classes', 'Sports & Fitness', 'Gardening', 'Cultural Classes',
  'Yoga / Meditation', 'Audio / Visual', 'Marketing', 'Reception', 'Food & Kitchen',
  'Montessori School', 'Special Projects', 'Temple Cleaning', 'Decoration', 'Misc Seva',
  'Health & Safety',
];

const today = () => new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
const todayISO = () => new Date().toISOString().slice(0, 10);

const initForm = {
  firstName: '', lastName: '', email: '', phone: '',
  age: 'Child', parentName: '', comments: '', centerIdField: '',
  sevaActivities: [],
  emergencyName: '', emergencyCell: '', signature: '',
};

const initWaiver = {
  volunteerName: '',
};

const initErrors = {};

export default function VolunteerRegistration() {
  const navigate = useNavigate();
  const abortRef = useRef(null);

  const [centers, setCenters] = useState([]);
  const [form, setForm] = useState(initForm);
  const [errors, setErrors] = useState(initErrors);
  const [waiver, setWaiver] = useState(initWaiver);
  const [waiverErrors, setWaiverErrors] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const centerFetchedRef = useRef(false);

  useEffect(() => {
    setCenters([{ id: 2, name: 'Atlanta' }]);
    setForm(prev => ({ ...prev, centerIdField: '2' }));

    if (centerFetchedRef.current) return;
    centerFetchedRef.current = true;

    volunteerService.getCenters()
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        const found = list.find(c => Number(c.center_Id ?? c.Center_id ?? c.centerId ?? 0) === 2);
        if (found) {
          const id = found.center_Id ?? found.Center_id ?? found.centerId ?? 2;
          const name = found.center_Name ?? found.Center_Name ?? found.center_name ?? 'Atlanta';
          setCenters([{ id: Number(id), name }]);
        }
      })
      .catch(() => {});

    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, []);

  const handleField = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => { const e = { ...prev }; delete e[field]; return e; });
  }, []);

  const toggleSeva = useCallback((seva) => {
    setForm(prev => {
      const list = prev.sevaActivities.includes(seva)
        ? prev.sevaActivities.filter(s => s !== seva)
        : [...prev.sevaActivities, seva];
      return { ...prev, sevaActivities: list };
    });
  }, []);

  const handleWaiverField = useCallback((field, value) => {
    setWaiver(prev => ({ ...prev, [field]: value }));
    setWaiverErrors(prev => { const e = { ...prev }; delete e[field]; return e; });
  }, []);

  const openModal = () => {
    const fullName = `${form.firstName} ${form.lastName}`.trim();
    setWaiver(prev => ({ ...prev, volunteerName: prev.volunteerName || fullName }));
    setShowModal(true);
  };

  const handleAgree = () => {
    setTermsAccepted(true);
    setShowModal(false);
    setErrors(prev => { const e = { ...prev }; delete e.terms; return e; });
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required';
    else if (form.firstName.trim().length > 50) e.firstName = 'Max 50 characters';
    if (!form.lastName.trim()) e.lastName = 'Last name is required';
    else if (form.lastName.trim().length > 50) e.lastName = 'Max 50 characters';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email format';
    if (!form.phone.trim()) e.phone = 'Phone is required';
    else if (!/^[0-9]{10}$/.test(form.phone.replace(/[\s\-().+]/g, ''))) e.phone = 'Enter a valid 10-digit phone number';
    if (!form.centerIdField) e.centerIdField = 'Please select a center';
    if (form.sevaActivities.length === 0) e.sevaActivities = 'Please select at least one seva activity';
    if (form.age === 'Child' && !form.parentName.trim()) e.parentName = 'Parent/Guardian name is required';
    if (!form.emergencyName.trim()) e.emergencyName = 'Emergency contact name is required';
    if (!form.emergencyCell.trim()) e.emergencyCell = 'Emergency contact cell is required';
    if (!form.signature.trim()) e.signature = 'Signature is required';
    if (!termsAccepted) e.terms = 'You must read and agree to the terms and conditions';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    setSubmitting(true);
    try {
      const payload = {
        FirstName: form.firstName,
        LastName: form.lastName,
        Email: form.email,
        Phone: form.phone,
        Age: form.age,
        Seva: form.sevaActivities.join(', ') || 'NA',
        CreatedDate: todayISO(),
        ParentName: form.age === 'Child' ? form.parentName : 'NA',
        Comments: form.comments || 'NA',
        Center_Id: parseInt(form.centerIdField),
        Emergency_Name: form.emergencyName || 'NA',
        Emergency_Con: form.emergencyCell || 'NA',
        Singnature: form.signature || 'NA',
        SingnatureDate: new Date().toISOString(),
      };

      await volunteerService.insertVolunteer(payload, abortRef.current.signal);

      const selectedCenter = centers.find(c => c.id === parseInt(form.centerIdField));
      const centerName = selectedCenter?.name ?? 'Atlanta';

      // s() ensures every field is a non-null, non-empty string for backend Required validation
      const s = (v) => (v !== null && v !== undefined && String(v).trim() !== '') ? String(v) : 'NA';

      const emailPayload = {
        emailFrom: 'NA',
        emailTo: s(form.email),
        emailSubject: 'NA',
        emailBody: 'NA',
        attachment: 'N',
        userPhoneNumber: s(form.phone),
        location: 'VolunteerRegistration',
        appContent: {
          userName: s(form.signature),
          programName: s(`${form.firstName} ${form.lastName}`.trim()),
          appSchedule: s(form.sevaActivities.join(', ')),
          attendee: s(centerName),
          date: s(today()),
          orderType: s(form.emergencyCell),
          orderNo: 'NA',
          paymentMode: s(form.parentName),
          amount: '0',
          email: s(form.phone),
          phone: s(form.phone),
          pickupPerson: s(form.signature),
          pickupPersonContact: 'NA',
          instructions: 'NA',
          itemData: s(today()),
          pickUpDate: 'NA',
          pickUpTime: s(form.email),
          paymentType: 'NA',
          billingAddress: 'NA',
          address: 'NA',
          pickupOrReceiver: 'NA',
          orderReady: s(form.email),
          orderReadyLastLine: 'NA',
          headingDate: s(form.emergencyName),
          headingTime: 'NA',
          startDate: s(todayISO()),
          endDate: s(todayISO()),
          transactionId: 'NA',
        },
      };

      volunteerService.sendConfirmationEmail(emailPayload, abortRef.current.signal)
        .then(() => setEmailSent(true))
        .catch(() => setEmailSent(false));

      setSubmitted(true);
    } catch (err) {
      if (err.name !== 'AbortError') {
        setErrors({ submit: 'Submission failed. Please try again.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="volreg-page">
        <div className="volreg-container">
          <div className="volreg-card">
            <div className="volreg-success">
              <div className="volreg-success-icon">✓</div>
              <h2 className="volreg-success-title">Registration Complete!</h2>
              {emailSent ? (
                <p className="volreg-success-sub">
                  Thank you for volunteering! A confirmation email has been sent to <strong>{form.email}</strong>.
                </p>
              ) : (
                <p className="volreg-success-sub">
                  Thank you for volunteering! We have received your registration.
                  <span className="volreg-success-note"> Note: We could not send a confirmation email at this time — please keep a note of your registration.</span>
                </p>
              )}
              <button className="volreg-success-back-btn" onClick={() => navigate('/')}>
                ← Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="volreg-page">

      {/* ── Dark Hero Banner ── */}
      <div className="volreg-hero">
        <div className="volreg-hero-inner">
          <div className="volreg-hero-text">
            <p className="volreg-hero-desc">
              The success of YugalKunj (YK) depends upon enthusiastic, selfless volunteers who are keen to give back their talent and time to the community in which they live. Whether you foresee yourself as an occasional volunteer committing to a few hours every month or as a committed member who can volunteer for several hours in a week, <strong style={{color:'#b45309',fontWeight:700}}>we need you!</strong>
            </p>
          </div>
          <div className="volreg-hero-award">
            🏅 YugalKunj is approved as an official Certifying Organization for the <strong>President's Volunteer Service Award</strong> and we have been able to recognize the great work and commitment of our youth as well as adult volunteers. This not only delivers a powerful message during our college application process, provides the necessary motivation to our continuing volunteers but also encourages others to take action.
          </div>
        </div>
      </div>

      <div className="volreg-container">
        <div className="volreg-card">
          <form onSubmit={handleSubmit} noValidate>

            {/* ── Section: Personal Info ── */}
            <p className="volreg-section-title">
              <span>👤</span> Personal Information
            </p>

            <div className="volreg-grid-2">
              <div className="volreg-field">
                <label className="volreg-label">First Name <span className="req">*</span></label>
                <input
                  className={`volreg-input${errors.firstName ? ' error' : ''}`}
                  type="text" maxLength={50} placeholder="First name"
                  value={form.firstName}
                  onChange={e => handleField('firstName', e.target.value)}
                />
                {errors.firstName && <span className="volreg-error-msg">{errors.firstName}</span>}
              </div>

              <div className="volreg-field">
                <label className="volreg-label">Last Name <span className="req">*</span></label>
                <input
                  className={`volreg-input${errors.lastName ? ' error' : ''}`}
                  type="text" maxLength={50} placeholder="Last name"
                  value={form.lastName}
                  onChange={e => handleField('lastName', e.target.value)}
                />
                {errors.lastName && <span className="volreg-error-msg">{errors.lastName}</span>}
              </div>

              <div className="volreg-field">
                <label className="volreg-label">Email <span className="req">*</span></label>
                <input
                  className={`volreg-input${errors.email ? ' error' : ''}`}
                  type="email" maxLength={100} placeholder="your@email.com"
                  value={form.email}
                  onChange={e => handleField('email', e.target.value)}
                />
                {errors.email && <span className="volreg-error-msg">{errors.email}</span>}
              </div>

              <div className="volreg-field">
                <label className="volreg-label">Phone <span className="req">*</span></label>
                <input
                  className={`volreg-input${errors.phone ? ' error' : ''}`}
                  type="tel" maxLength={20} placeholder="(999)-999-9999"
                  value={form.phone}
                  onChange={e => handleField('phone', e.target.value)}
                />
                {errors.phone && <span className="volreg-error-msg">{errors.phone}</span>}
              </div>
            </div>

            {/* Center */}
            <div className="volreg-field mt-2">
              <label className="volreg-label">Center <span className="req">*</span></label>
              <select
                className={`volreg-select${errors.centerIdField ? ' error' : ''}`}
                value={form.centerIdField}
                onChange={e => handleField('centerIdField', e.target.value)}
              >
                <option value="">— Select Center —</option>
                {centers.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {errors.centerIdField && <span className="volreg-error-msg">{errors.centerIdField}</span>}
            </div>

            <hr className="volreg-divider" />

            {/* ── Section: Seva Activities ── */}
            <p className="volreg-section-title">
              <span>🙏</span> Volunteer Activities / Seva
            </p>
            {errors.sevaActivities && <span className="volreg-error-msg mb-2 block">{errors.sevaActivities}</span>}
            <div className="volreg-seva-grid">
              {SEVA_OPTIONS.map(seva => (
                <label
                  key={seva}
                  className={`volreg-seva-item${form.sevaActivities.includes(seva) ? ' checked' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={form.sevaActivities.includes(seva)}
                    onChange={() => toggleSeva(seva)}
                  />
                  <span className="volreg-seva-label">{seva}</span>
                </label>
              ))}
            </div>

            <hr className="volreg-divider" />

            {/* ── Section: Age ── */}
            <p className="volreg-section-title">
              <span>📋</span> Age Confirmation
            </p>
            <div className="volreg-toggle-group">
              {[
                { value: 'Adult', label: '18 years and older' },
                { value: 'Child', label: 'Parent / Legal Guardian' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  className={`volreg-toggle-btn${form.age === opt.value ? ' active' : ''}`}
                  onClick={() => handleField('age', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {form.age === 'Child' && (
              <div className="volreg-field mt-2">
                <label className="volreg-label">Parent / Guardian Name <span className="req">*</span></label>
                <input
                  className={`volreg-input${errors.parentName ? ' error' : ''}`}
                  type="text" maxLength={50} placeholder="Full name"
                  value={form.parentName}
                  onChange={e => handleField('parentName', e.target.value)}
                />
                {errors.parentName && <span className="volreg-error-msg">{errors.parentName}</span>}
              </div>
            )}

            <hr className="volreg-divider" />

            {/* ── Section: Comments ── */}
            <p className="volreg-section-title">
              <span>💬</span> Comments
            </p>
            <textarea
              className="volreg-textarea" rows={2} placeholder="Any additional notes..."
              value={form.comments}
              onChange={e => handleField('comments', e.target.value)}
            />

            <hr className="volreg-divider" />

            {/* ── Section: Emergency Contact ── */}
            <p className="volreg-section-title">
              <span>🚨</span> Emergency Contact
            </p>
            <div className="volreg-grid-2">
              <div className="volreg-field">
                <label className="volreg-label">Contact Name <span className="req">*</span></label>
                <input
                  className={`volreg-input${errors.emergencyName ? ' error' : ''}`}
                  type="text" maxLength={100} placeholder="Full name"
                  value={form.emergencyName}
                  onChange={e => handleField('emergencyName', e.target.value)}
                />
                {errors.emergencyName && <span className="volreg-error-msg">{errors.emergencyName}</span>}
              </div>
              <div className="volreg-field">
                <label className="volreg-label">Contact Cell Number <span className="req">*</span></label>
                <input
                  className={`volreg-input${errors.emergencyCell ? ' error' : ''}`}
                  type="tel" maxLength={20} placeholder="(999)-999-9999"
                  value={form.emergencyCell}
                  onChange={e => handleField('emergencyCell', e.target.value)}
                />
                {errors.emergencyCell && <span className="volreg-error-msg">{errors.emergencyCell}</span>}
              </div>
            </div>

            <hr className="volreg-divider" />

            {/* ── Section: Signature ── */}
            <p className="volreg-section-title">
              <span>✍️</span> Signature
            </p>
            <div className="volreg-grid-2">
              <div className="volreg-field">
                <label className="volreg-label">Sign your name (typed) <span className="req">*</span></label>
                <input
                  className={`volreg-input${errors.signature ? ' error' : ''}`}
                  type="text" maxLength={100} placeholder="Type full name as signature"
                  value={form.signature}
                  onChange={e => handleField('signature', e.target.value)}
                />
                {errors.signature && <span className="volreg-error-msg">{errors.signature}</span>}
              </div>
              <div className="volreg-field">
                <label className="volreg-label">Date</label>
                <input className="volreg-input" type="text" readOnly value={today()} />
              </div>
            </div>

            <hr className="volreg-divider" />

            {/* ── Terms & Conditions ── */}
            <div
              className={`volreg-terms-row${errors.terms ? ' border-red-400' : ''}`}
              style={{cursor: 'pointer'}}
              onClick={() => setTermsAccepted(prev => !prev)}
            >
              <div
                className={`volreg-custom-cb${termsAccepted ? ' checked' : ''}`}
                role="checkbox"
                aria-checked={termsAccepted}
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === ' ') { e.preventDefault(); setTermsAccepted(prev => !prev); } }}
              >
                {termsAccepted && (
                  <svg viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg" width="10" height="10">
                    <path d="M1 5l3.5 3.5L11 1" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <span className="volreg-terms-text">
                I (and parent or guardian if Volunteer is under 18) have carefully read the release and voluntarily agree to the{' '}
                <span
                  className="volreg-terms-link"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); openModal(); }}
                >
                  Terms &amp; Conditions / Liability Waiver
                </span>
              </span>
            </div>
            {errors.terms && <span className="volreg-error-msg mt-1 block">{errors.terms}</span>}

            {errors.submit && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                {errors.submit}
              </div>
            )}

            <div className="mt-6">
              <button type="submit" className="volreg-submit-btn" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit Volunteer Registration'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* ── Waiver Modal ── */}
      {showModal && (
        <div className="volreg-modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="volreg-modal" onClick={e => e.stopPropagation()}>
            <div className="volreg-modal-header">
              <span className="volreg-modal-title">🤝 Volunteer Liability Waiver</span>
              <button className="volreg-modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>

            <div className="volreg-modal-body">
              <div className="volreg-modal-org">
                <h2>Radha Madhav Society (RMS) / Harmony Youth Center (HYC)</h2>
                <p>2769 Duluth Highway, Duluth, GA 30096</p>
                <p className="font-bold text-indigo-700 mt-1">Volunteer Terms, Conditions &amp; Liability Waiver</p>
              </div>

              <div className="volreg-waiver-section">
                <p className="text-sm text-gray-600 italic">
                  Yugal Kunj is grateful to you for offering your time and talent in furtherance of Yugal Kunj vision.
                  Every volunteer is requested to follow the rules and regulations.
                </p>
              </div>

              <div className="volreg-waiver-section">
                <p className="volreg-waiver-section-title">I understand and agree to the following:</p>
                {[
                  ['1.', 'I am donating my time services and shall at no time be considered an employee or contractor of the Radha Madhav Society.'],
                  ['2.', 'Volunteers are not official representatives of RMS/HYC and cannot act on behalf, unless given specific written instructions by the Board of Directors.'],
                  ['3.', 'Volunteers are encouraged to help each other and work together to create environment of selflessness, humility and harmony on the premises of Yugal Kunj.'],
                  ['4.', 'Volunteers should not engage themselves in any activity/solicitation/campaign of any nature for personal gain on the premises of Yugal Kunj or utilize information and contacts obtained at RMS/HYC for such purposes.'],
                  ['5.', 'Volunteering is an honorable act, and volunteers are kindly requested to refrain from escalating any disagreements. Volunteers are encouraged to bring any issues or disputes to the Board for clarification and/or resolution.'],
                  ['6.', 'RMS/HYC may revoke volunteer privileges of individuals at any time, without providing any notice or reason.'],
                  ['7.', 'Any incident or behavior which does not conform to the Rules and Regulation or Code of Conduct of RMS/HYC should be brought to the notice of the Board in writing. The Board will try its best to treat all such matters in a confidential manner.'],
                ].map(([num, text]) => (
                  <div key={num} className="flex gap-2 mb-2">
                    <span className="text-sm font-bold text-indigo-600 flex-shrink-0">{num}</span>
                    <p className="text-sm text-gray-600 leading-relaxed">{text}</p>
                  </div>
                ))}
              </div>

              <div className="volreg-waiver-section">
                <p className="volreg-waiver-section-title">Health and Medical Waiver</p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  I affirm that I am physically fit and able to participate in the volunteer activities assigned to me.
                  I assume all risk of personal injury, sickness and expenses incurred as a result of any accident while
                  volunteering for Radha Madhav Society (RMS) / Harmony Youth Center. I release RMS / HYC its directors,
                  officers, agents and volunteers from any liability related to any personal injury suffered by me while
                  volunteering. I further agree to accept any decisions or actions made by other RMS / HYC volunteers in
                  seeking medical care for me in an emergency situation, and agree to pay all the costs and fees for the
                  medical care or treatment provided to me.
                </p>
              </div>

              <div className="volreg-waiver-section">
                <p className="volreg-waiver-section-title">Photographic Release</p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Radha Madhav Society (RMS) / Harmony Youth Center (HYC) may take videos, photographs or testimonials
                  of volunteers and use the same on their websites, in advertisements, or to communicate about the
                  activities at the center. I grant and convey to RMS / HYC all rights, title, and interests in any and
                  all photographs, images, video, testimonials, audio in connection with my providing volunteer services
                  for RMS / HYC.
                </p>
              </div>

              <div className="volreg-waiver-section">
                <p className="volreg-waiver-section-title">Waiver of Liability, Release and Indemnification</p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  I waive and release Radha Madhav Society (RMS) / Harmony Youth Center (HYC), its officers, agents,
                  representatives, employees, and volunteers (collectively the "Releasees") from any and all liability,
                  claims, causes of action and/or suits arising out of or in connection with any injuries, illness,
                  damage or death sustained by myself while a volunteer or while using any of the facilities located at
                  2769 Duluth Highway, Duluth, GA 30096. I hereby agree to indemnify and hold harmless Radha Madhav
                  Society (RMS), its officers, agents, representatives, employees, and volunteers with respect to any
                  claim asserted as a result of property damage, personal injury, illness, damage or death, which
                  indemnification and hold harmless includes any and all attorney's fees and costs.
                </p>
              </div>

            </div>

            <div className="volreg-modal-footer">
              <button className="volreg-modal-close-btn" onClick={() => setShowModal(false)}>Close</button>
              <button className="volreg-modal-agree-btn" onClick={handleAgree}>I Agree</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
